import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import client, { getSession } from "@/lib/api";
import Divider from "@/components/Divider";

export default function MenuPage() {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [allRecipes, setAllRecipes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getSession()) { navigate("/login"); return; }
    Promise.all([
      client.get("/content/menus"),
      client.get("/content/recipes?preview=true"),
    ]).then(([m, r]) => {
      const found = m.data.menus.find(x => x.slug === slug);
      setMenu(found);
      setAllRecipes(r.data.recipes);
    }).catch(() => navigate("/read"));
  }, [slug, navigate]);

  if (!menu) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl">Loading menu…</div>;

  const recipeMap = Object.fromEntries(allRecipes.map(r => [r.slug, r]));

  return (
    <div className="bg-mizizi-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        <Link to="/read" data-testid="back-reader" className="overline text-mizizi-terracotta hover:text-mizizi-gold">← Table of Contents</Link>
      </div>
      <header className="max-w-4xl mx-auto px-6 md:px-10 pb-12 text-center">
        <div className="overline text-mizizi-gold">Menu</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-4 leading-none text-mizizi-charcoal">{menu.title}</h1>
        <p className="mt-6 italic text-mizizi-charcoal/70 text-lg">{menu.subtitle}</p>
      </header>

      <Divider />

      <section className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="overline text-mizizi-terracotta text-center">Timeline</div>
        <ol className="mt-8 space-y-1">
          {menu.timeline.map((t, i) => (
            <li key={`${t.time}-${t.task}`} className="grid grid-cols-[140px_1fr] gap-6 py-4 hairline-b" data-testid={`timeline-${i}`}>
              <div className="overline text-mizizi-gold">{t.time}</div>
              <div className="font-serif text-lg text-mizizi-charcoal">{t.task}</div>
            </li>
          ))}
        </ol>
      </section>

      <Divider />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        <div className="overline text-mizizi-terracotta text-center">On the Table</div>
        <div className="mt-8 space-y-3">
          {menu.recipes.map((s) => {
            const r = recipeMap[s];
            if (!r) return null;
            return (
              <Link
                key={s}
                to={`/read/recipe/${s}`}
                data-testid={`menu-recipe-${s}`}
                className="flex items-center justify-between p-6 border border-mizizi-gold/30 hover:bg-mizizi-forest hover:text-mizizi-cream transition-colors group"
              >
                <div>
                  <div className="overline text-mizizi-gold group-hover:text-mizizi-gold">{r.chapter}</div>
                  <div className="font-serif text-2xl mt-1">{r.title}</div>
                </div>
                <div className="overline text-mizizi-charcoal/50 group-hover:text-mizizi-cream/70">{r.prep_min + r.cook_min}′ →</div>
              </Link>
            );
          })}
        </div>
      </section>

      {menu.shopping_list && (
        <section className="bg-mizizi-forest text-mizizi-cream py-20" data-testid={`shopping-list-${menu.slug}`}>
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="overline text-mizizi-gold text-center">Shopping List</div>
            <h2 className="font-serif text-4xl md:text-5xl text-center mt-4">Before you start.</h2>
            <div className="mt-12 grid md:grid-cols-2 gap-12">
              <div>
                <div className="overline text-mizizi-gold hairline-b pb-3 border-mizizi-gold/40">Produce</div>
                <ul className="mt-5 space-y-2 font-serif text-lg">
                  {menu.shopping_list.produce.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="text-mizizi-gold mt-1.5 text-xs">◇</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="overline text-mizizi-gold hairline-b pb-3 border-mizizi-gold/40">Pantry</div>
                <ul className="mt-5 space-y-2 font-serif text-lg">
                  {menu.shopping_list.pantry.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="text-mizizi-gold mt-1.5 text-xs">◇</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {menu.shopping_list.make_ahead?.length > 0 && (
              <div className="mt-14 max-w-2xl mx-auto text-center">
                <div className="overline text-mizizi-gold">Make ahead</div>
                <ul className="mt-4 space-y-2 italic text-mizizi-cream/85 leading-relaxed">
                  {menu.shopping_list.make_ahead.map((tip) => (
                    <li key={tip}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
